(function () {
  if (window.__filterPillsBound) return;
  window.__filterPillsBound = true;

  const PILL_SELECTOR = '.filter-pills__pill';
  const SECTION_SELECTOR = 'shop-component[data-section-id]';
  const LOADING_CLASS = 'filter-pills--loading';

  function findSection(el) {
    return el.closest(SECTION_SELECTOR) || document.querySelector(SECTION_SELECTOR);
  }

  function withSectionParam(url, sectionId) {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      return u.toString();
    } catch (e) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}section_id=${encodeURIComponent(sectionId)}`;
    }
  }

  function stripSectionParam(url) {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete('section_id');
      return u.pathname + (u.search ? u.search : '') + (u.hash || '');
    } catch (e) {
      return url;
    }
  }

  // Regions whose innerHTML we replace wholesale. Anything not in this list
  // stays put — drawers stay open, sort dropdown stays mounted, pill rail's
  // inline style + colors are preserved (only the pill list inside is rebuilt).
  const SWAP_REGIONS = [
    '.shop__products',
    '[data-filter-pills] .filter-pills__list',
    '#ShopProductCount',
    'shop-active-filters',
    '.sort-list-dropdown__active-sort-option',
  ];

  // Inputs we sync (checked/value) instead of replacing — preserves focus,
  // open drawers, and any animation state.
  const INPUT_SYNC_SELECTOR =
    'form[data-filters-form] input, input[name="sort_by"]';

  let isSwapping = false;

  function syncInputs(sectionEl, incomingRoot) {
    const currentInputs = sectionEl.querySelectorAll(INPUT_SYNC_SELECTOR);
    currentInputs.forEach((current) => {
      if (!current.name) return;
      const type = current.type;
      let incoming;
      if (type === 'checkbox' || type === 'radio') {
        try {
          incoming = incomingRoot.querySelector(
            `input[name="${CSS.escape(current.name)}"][value="${CSS.escape(current.value)}"]`
          );
        } catch (_) {
          incoming = null;
        }
        if (incoming && current.checked !== incoming.checked) {
          current.checked = incoming.checked;
        }
      } else if (type !== 'submit' && type !== 'button') {
        try {
          incoming = incomingRoot.querySelector(
            `input[name="${CSS.escape(current.name)}"]`
          );
        } catch (_) {
          incoming = null;
        }
        if (incoming && current.value !== incoming.value) {
          current.value = incoming.value;
        }
      }
    });
  }

  async function fetchAndSwap(targetUrl, section) {
    const sectionId = section.dataset.sectionId;
    if (!sectionId) return;

    section.classList.add(LOADING_CLASS);
    isSwapping = true;

    try {
      const fetchUrl = withSectionParam(targetUrl, sectionId);
      const res = await fetch(fetchUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error(`Section render fetch failed: ${res.status}`);
      const html = await res.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incomingSection =
        doc.querySelector(`shop-component[data-section-id="${sectionId}"]`) ||
        doc.querySelector(SECTION_SELECTOR) ||
        doc.body;

      // Targeted region swaps.
      SWAP_REGIONS.forEach((selector) => {
        const current = section.querySelector(selector);
        const incoming = incomingSection.querySelector(selector);
        if (current && incoming) {
          current.innerHTML = incoming.innerHTML;
        }
      });

      // Sync drawer checkboxes + sort radios in place (no DOM replacement).
      syncInputs(section, incomingSection);

      // Mirror pill rail active state onto its container (`aria-current`, color
      // CSS, etc. are inside .filter-pills__list which we replaced — so the
      // outer <nav>'s inline style stays intact for theming).

      window.history.pushState({ filterPills: true }, '', stripSectionParam(targetUrl));

      document.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId },
        })
      );
    } catch (err) {
      console.warn('[filter-pills] AJAX swap failed, falling back to page load:', err);
      window.location.href = stripSectionParam(targetUrl);
    } finally {
      section.classList.remove(LOADING_CLASS);
      isSwapping = false;
    }
  }

  // ----- Pill click handling (event delegation, survives re-renders) -----
  document.addEventListener('click', (e) => {
    const pill = e.target.closest(PILL_SELECTOR);
    if (!pill) return;

    // Allow modified clicks to behave normally (new tab, etc.).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    const section = findSection(pill);
    if (!section) return;

    e.preventDefault();
    fetchAndSwap(pill.getAttribute('href'), section);
  });

  // ----- Drawer filter form: AJAX on any input change -----
  function buildUrlFromForm(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value === '' || value === 'false') continue;
      params.append(key, value);
    }
    const baseUrl = (form.getAttribute('action') || window.location.pathname).split('?')[0];
    const qs = params.toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
  }

  // Filter form change — capture phase so we beat the theme's bubble-phase reload.
  document.addEventListener(
    'change',
    (e) => {
      if (isSwapping) return;
      const input = e.target;
      if (!input || !input.closest) return;

      // Sort radio (desktop dropdown + mobile sidebar)
      if (input.matches('input[name="sort_by"]')) {
        const section = findSection(input);
        if (!section) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', input.value);
        url.searchParams.delete('page');
        fetchAndSwap(url.pathname + url.search, section);
        return;
      }

      // Filter form input change
      const form = input.closest('form[data-filters-form]');
      if (form) {
        const section = findSection(form);
        if (!section) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        fetchAndSwap(buildUrlFromForm(form), section);
      }
    },
    true
  );

  // Filter form explicit submit (e.g. Enter key, mobile "Apply" button).
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || !form.matches) return;
      if (!form.matches('form[data-filters-form], form.sort-list-dropdown__form')) return;

      const section = findSection(form);
      if (!section) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      fetchAndSwap(buildUrlFromForm(form), section);
    },
    true
  );

  // ----- Active filter chips + "Clear all" reset button -----
  document.addEventListener(
    'click',
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      // Either the "Clear all" reset or a single chip's remove button — both
      // are filter-remove-btn custom elements with a data-url attribute on the
      // element or its inner anchor.
      const removeBtn = e.target.closest('filter-remove-btn, [data-filters-reset], [data-filter-remove]');
      if (!removeBtn) return;

      const section = findSection(removeBtn);
      if (!section) return;

      // Prefer href on inner anchor; fall back to data-url on the element.
      const innerLink = removeBtn.querySelector('a[href]');
      const url = innerLink ? innerLink.getAttribute('href') : removeBtn.getAttribute('data-url');
      if (!url) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      fetchAndSwap(url, section);
    },
    true
  );

  // Handle browser back/forward.
  window.addEventListener('popstate', (e) => {
    const section = document.querySelector(SECTION_SELECTOR);
    if (!section) return;
    if (!e.state || !e.state.filterPills) return;
    fetchAndSwap(window.location.pathname + window.location.search, section);
  });
})();
